/**
 * HelpButton Component
 *
 * A small contextual help button ("?") that opens a HelpPopup for the given feature.
 * Place this anywhere in the app with a featureKey to provide inline help.
 *
 * Usage: <HelpButton featureKey="patients_list" />
 */

import { useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Question } from "@phosphor-icons/react";
import { HELP_CONTENT } from "../../config/helpContent";
import { trackFeature, FEATURE_KEYS } from "../../services/telemetry";
import HelpPopup from "./HelpPopup";
import "./HelpButton.css";

function HelpButton({ featureKey }) {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState(null);
  const [tooltipPlacement, setTooltipPlacement] = useState("above");
  const btnRef = useRef(null);

  const helpData = HELP_CONTENT[featureKey];

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      setIsPopupOpen(true);

      // Batched telemetry counter (replaces the legacy 1-doc-per-click log)
      trackFeature(FEATURE_KEYS.HELP);
    },
    []
  );

  const handleClosePopup = useCallback(() => {
    setIsPopupOpen(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const spaceAbove = rect.top;
    const gap = 8;
    const margin = 12;
    const vw = window.innerWidth;

    const isBelow = spaceAbove < 50;
    const topVal = isBelow ? rect.bottom + gap : rect.top - gap;

    let left = centerX;
    let xTransform = "-50%";
    let arrowLeft = "50%";

    if (centerX < 100) {
      left = margin;
      xTransform = "0%";
      arrowLeft = `${Math.max(10, centerX - margin)}px`;
    } else if (centerX > vw - 100) {
      left = vw - margin;
      xTransform = "-100%";
      arrowLeft = `calc(100% - ${Math.max(10, vw - margin - centerX)}px)`;
    }

    setTooltipPlacement(isBelow ? "below" : "above");
    setTooltipStyle({
      position: "fixed",
      top: topVal,
      left,
      transform: `translate(${xTransform}${isBelow ? "" : ", -100%"})`,
      "--arrow-left": arrowLeft,
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTooltipStyle(null);
  }, []);

  if (!helpData) {
    console.warn(`[CK-HELP] No help content found for key: "${featureKey}"`);
    return null;
  }

  return (
    <>
      <div
        className="help-button-wrapper"
        ref={btnRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          className="help-button"
          onClick={handleClick}
          type="button"
        >
          <Question size={16} weight="bold" />
        </button>
      </div>

      {tooltipStyle &&
        createPortal(
          <span
            className={`help-button-tooltip visible ${tooltipPlacement === "below" ? "tooltip-below" : "tooltip-above"}`}
            style={tooltipStyle}
          >
            הסבר על {helpData.tooltipLabel}
          </span>,
          document.body
        )}

      {isPopupOpen && (
        <HelpPopup
          featureKey={featureKey}
          helpData={helpData}
          onClose={handleClosePopup}
        />
      )}
    </>
  );
}

export default HelpButton;
